/**
 * PhoneInput.tsx
 *
 * The one phone field for every BotSpot form (Rob, 2026-10-19).
 *
 * WHY THIS EXISTS:
 * - People type phones in every shape. This field formats what they see
 *   ((416) 555-1234) and hands the page a submit-ready value: E.164 when the
 *   number parses, otherwise exactly what they typed. Pages send that value
 *   as-is, so no submit path needs its own phone logic.
 * - It is a plain <input>. It carries no styles of its own: each page passes
 *   its existing input class, so every form keeps its current look.
 *
 * IMPORTANT BEHAVIORS:
 * - Never blocks, never marks the field invalid, never shows an error.
 * - Formats on blur, not while typing. As-you-type formatting moved the caret,
 *   fought backspace over punctuation, and guessed wrong on partial numbers
 *   (1234567890 became "1 (234) 567-890"). While focused the field shows
 *   exactly what was typed; on blur a valid number is shown as
 *   (416) 555-1234 or +44 7911 123456, and anything else stays as typed.
 * - Blur only changes the display. It never calls onValueChange, because pages
 *   reset number-specific SMS consent whenever the number changes.
 * - Pages set the tel input type themselves (the SMS consent coverage guard finds
 *   phone forms by that attribute).
 */
import React, { useEffect, useRef, useState } from 'react';
import {
  formatPhoneForDisplay,
  isPhoneLibraryLoaded,
  loadPhoneLibrary,
  toValidE164,
} from '../../utils/phoneNumber';

export type PhoneInputProps = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'value' | 'defaultValue' | 'onChange'
> & {
  /** The page's stored value (E.164, raw text, or empty). */
  value: string | null | undefined;
  /** Receives E.164 when parseable, otherwise exactly what was typed. */
  onValueChange: (value: string) => void;
};

const PhoneInput = React.forwardRef<HTMLInputElement, PhoneInputProps>(function PhoneInput(
  { value, onValueChange, onBlur, onFocus, inputMode = 'tel', autoComplete = 'tel', ...inputProps },
  ref,
) {
  const external = value ?? '';
  const [text, setText] = useState(() => formatPhoneForDisplay(external));
  const [libraryReady, setLibraryReady] = useState(isPhoneLibraryLoaded);
  const lastEmitted = useRef(external);
  const focused = useRef(false);

  useEffect(() => {
    if (libraryReady) return undefined;
    let live = true;
    loadPhoneLibrary().then((library) => {
      if (live && library) setLibraryReady(true);
    });
    return () => {
      live = false;
    };
  }, [libraryReady]);

  // The page changed the value itself (prefill, saved profile, reset).
  useEffect(() => {
    if (external === lastEmitted.current) return;
    lastEmitted.current = external;
    setText(formatPhoneForDisplay(external));
  }, [external]);

  // Metadata arrived after a prefilled value rendered: tidy it, display only.
  useEffect(() => {
    if (libraryReady && !focused.current) setText((current) => formatPhoneForDisplay(current));
  }, [libraryReady]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const typed = event.target.value;
    setText(typed);
    const submitValue = toValidE164(typed) ?? typed;
    lastEmitted.current = submitValue;
    onValueChange(submitValue);
  };

  const handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    focused.current = true;
    onFocus?.(event);
  };

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    focused.current = false;
    setText((current) => formatPhoneForDisplay(current));
    onBlur?.(event);
  };

  return (
    <input
      ref={ref}
      {...inputProps}
      inputMode={inputMode}
      autoComplete={autoComplete}
      value={text}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
    />
  );
});

export default PhoneInput;
